import axios from "axios";
import { useAuthUserStore } from "@/stores/authUser";
import { useFormErrorsStore } from "@/stores/formErrors";

/**
 * Create a new Axios client instance
 * @see https://github.com/mzabriskie/axios#creating-an-instance
 */
const getClient = (baseUrl = null) => {
    const options = {
        baseURL: baseUrl
    };

    // if (store.getters["users/isAuthenticated"]) {
    //     options.headers = {
    //         Authorization: `Bearer ${store.getters["users/accessToken"]}`
    //     };
    // }

    options.headers = {
        "X-Requested-With": "XMLHttpRequest"
    };

    const client = axios.create(options);

    // Add a request interceptor
    client.interceptors.request.use(
        requestConfig => requestConfig,
        requestError => {
            //Send request exception to error api

            return Promise.reject(requestError);
        }
    );

    // Add a response interceptor
    client.interceptors.response.use(
        response => {
            if ((response.status === 204 || response.status === 201)
                && (response.data === "" || (response.data?.data && Array.isArray(response.data.data) && response.data.data.length === 0 ))
                && "x-resource-id" in response.headers) {
                response.data = {
                    id: response.headers["x-resource-id"]
                };
            }
            return response;
        },
        error => {
            const authUserStore = useAuthUserStore();
            const formErrorsStore = useFormErrorsStore();
            error.shouldHandle = true;
            if (error.response.status >= 500) {
                //Send response exception to error api
            }

            if (
                !error.config.ignore401Reload &&
                error.response.status === 401
            ) {
                if (authUserStore.check) {
                    //if we have a user then clear the user from the state
                    authUserStore.resetAuthState().then(() => {
                        location.reload();
                    });
                }
            }

            if (error.response.status === 422) {
                error.shouldHandle = false;
                 //handle formdata vue state for errors with the form.
                formErrorsStore.setErrors(error.response.data.data.errors);
            }

            return Promise.reject(error);
        }
    );

    return client;
};

export class ApiClient {
    constructor(basePath = null, apiPath = null) {
        this.client = getClient(
            "https://" +
                window.location.host +
                (apiPath ?? import.meta.env.VITE_API_PATH) +
                basePath
        );
    }

    get(url, conf = {}) {
        return this.client
            .get(url, conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error));
    }

    delete(url, conf = {}) {
        return this.client
            .delete(url, conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error));
    }

    head(url, conf = {}) {
        return this.client
            .head(url, conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error));
    }

    options(url, conf = {}) {
        return this.client
            .options(url, conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error));
    }

    post(url, data = {}, conf = {}) {
        return this.client
            .post(url, data, conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error));
    }

    put(url, data = {}, conf = {}) {
        return this.client
            .put(url, data, conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error));
    }

    patch(url, data = {}, conf = {}) {
        return this.client
            .patch(url, data, conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error));
    }

    request(conf = {}) {
        return this.client
            .request(conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error));
    }
}

/**
 * Base HTTP Client
 */
export default {
    // Provide request methods with the default base_url
    get(url, conf = {}) {
        return getClient()
            .get(url, conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error));
    },

    delete(url, conf = {}) {
        return getClient()
            .delete(url, conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error));
    },

    head(url, conf = {}) {
        return getClient()
            .head(url, conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error));
    },

    options(url, conf = {}) {
        return getClient()
            .options(url, conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error));
    },

    post(url, data = {}, conf = {}) {
        return getClient()
            .post(url, data, conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error));
    },

    put(url, data = {}, conf = {}) {
        return getClient()
            .put(url, data, conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error));
    },

    patch(url, data = {}, conf = {}) {
        return getClient()
            .patch(url, data, conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error));
    },

    request(conf = {}) {
        return getClient()
            .request(conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error));
    }
};
